import React, { useEffect, useState } from 'react';
import { FormikErrors, useFormik } from 'formik';
import { useHistory } from 'react-router';
import { Tracking, VehicleDescriptionService } from 'api';
import { useApp, useDeposit } from 'providers';
import { Loader } from 'components';
import { ROUTES } from 'routes';
import { useLoading, useReturn } from 'hooks';
import { ApiError, VehicleDescription, WarrantyDesignation } from 'types';
import { formatDateReverse } from 'utils/date';
import { VehicleDescriptionFormView } from './VehicleDescriptionFormView';
import { LabeloService } from 'api/Dab/LabeloService';

const emptyVehicleTrait = { equipments: [], packs: [], options: [], repairs: [] };

export const VehicleDescriptionForm: React.FC<{ defaultErrorMessage?: string }> = () => {
  const history = useHistory();
  const { user } = useApp();
  const {
    vehicle: {
      versionId,
      category = localStorage.getItem('category'), // To remove after equipments API update
      firstCirculationDate,
      equipmentsOrOptions,
      repairs = [],
      duration,
      designation,
    },
    setEquipmentsAndOptions,
  } = useDeposit();

  const [{ equipments, options, packs }, setVehicleDescription] = useState<VehicleDescription>(emptyVehicleTrait);
  const [labelos, setLabelos] = useState<WarrantyDesignation[]>([]);
  const [errorMessage, setErrorMessage] = useState<string>();
  const { isLoading, toggleLoading } = useLoading(true);

  if (!(versionId && category && firstCirculationDate)) history.replace(ROUTES.vehicleVersionChoice);

  useEffect(() => {
    (async () => {
      if (versionId && category && firstCirculationDate) {
        try {
          const isFirstCall = !(equipmentsOrOptions && equipmentsOrOptions.length > 0);
          const newVehicleDescription = await VehicleDescriptionService.getVehicleEquipmentsOptionsPacks(
            versionId,
            category,
            formatDateReverse(firstCirculationDate),
            isFirstCall,
            equipmentsOrOptions,
          );

          if (newVehicleDescription) setVehicleDescription(newVehicleDescription);

          if (user?.isPro && user.username) {
            const newLabelos = await LabeloService.getLabelos(user?.username);
            if (newLabelos) {
              setLabelos(newLabelos);
            }
          }
        } catch (r) {
          setErrorMessage((r as ApiError).message);
        } finally {
          toggleLoading();
        }
      }
    })();
  }, [versionId, category, firstCirculationDate, equipmentsOrOptions, user, toggleLoading]);

  const formikBag = useFormik<VehicleDescription>({
    enableReinitialize: true,
    initialValues: { equipments, options, packs, repairs, duration, designation },
    onSubmit: async (values, { setErrors, setSubmitting }) => {
      const errors: FormikErrors<VehicleDescription> = {};

      if (!equipments || values.equipments?.length !== equipments?.length) {
        errors.equipments = 'Erreur dans la liste des équipements';
      }
      if (!options || values.options?.length !== options?.length) {
        errors.options = 'Erreur dans la liste des options';
      }
      if (!packs || values.packs?.length !== packs?.length) {
        errors.packs = 'Erreur dans la liste des packs';
      }

      if (Object.keys(errors).length !== 0) {
        setErrors(errors);
        setSubmitting(false);
        Tracking.sendCurrentPageError();
        return;
      }

      try {
        await setEquipmentsAndOptions({
          equipments: values.equipments.filter(e => e.selected),
          options: values.options.filter(o => o.selected),
          packs: values.packs.filter(p => p.selected),
          repairs: values.repairs.filter(p => p.selected),
          duration: values.duration,
          designation: values.designation,
        });
        history.push(ROUTES.photos);
      } catch (r) {
        Tracking.sendCurrentPageError();
        setErrorMessage((r as Error).message);
      }
    },
  });

  const onReturn = useReturn(ROUTES.vehicleVersionChoice);

  return isLoading ? (
    <Loader />
  ) : (
    <VehicleDescriptionFormView
      isPro={user?.isPro}
      labelos={labelos}
      {...formikBag}
      errorMessage={errorMessage}
      handleReturn={onReturn}
    />
  );
};
